import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  linkedSignal,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NzMessageService } from 'ng-zorro-antd/message';
import {
  SchedulerComponent,
  SchedulerEvent,
  SchedulerResource,
  SchedulerViewMode,
} from '../../components/scheduler/scheduler.component';

import { NzButtonComponent, NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconDirective } from 'ng-zorro-antd/icon';
import {
  NzSpaceComponent,
  NzSpaceModule,
  NzSpaceCompactComponent,
} from 'ng-zorro-antd/space';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';
import {
  NzContextMenuService,
  NzDropdownMenuComponent,
  NzDropDownModule,
} from 'ng-zorro-antd/dropdown';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { RouterLink, Router } from '@angular/router';
import { rxResource, takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { format } from 'date-fns';
import {
  BookingService,
  CalendarBookingItem,
  CalendarBookingService,
  DayHold,
  EmployeeService,
  transformDateTime,
  VisitService,
  VisitStatusPipe,
} from '@beautyapp/core';
import { interval, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AuthState } from '@beautyapp/auth';
import { Store } from '@ngxs/store';
import {
  AppointmentState,
  computeCalendarBookingAmounts,
} from '../../appointment.state';
import {
  CalendarTimeInterval,
  ClearAddAppointmentSpecService,
} from '../../appointment.actions';
import { AddAppointmentComponent } from '../../booking-modal/components/add-appointment/add-appointment.component';
import { AppointmentResourceOrderComponent } from '../../components/appointment-resource-order/appointment-resource-order.component';
import { BeautySchedulerEventPopoverTemplateDirective } from '../../components/scheduler/beauty-scheduler-event-popover.directive';
import { BeautySchedulerEventTemplateDirective } from '../../components/scheduler/beauty-scheduler-event.directive';
import { BeautySchedulerResourceTemplateDirective } from '../../components/scheduler/beauty-scheduler-resource.directive';
import { EmployeeViewComponent } from '@beautyapp/hr';
import { NzDrawerModule, NzDrawerService } from 'ng-zorro-antd/drawer';
import { VisitManageComponent } from '../../visit-modal/visit-manage.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { InputModule, DatepickerComponent } from '@beautyapp/form';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { FormsModule } from '@angular/forms';
import { NgxPermissionsModule } from 'ngx-permissions';

@Component({
  selector: 'beautyapp-scheduler-calendar',
  standalone: true,
  imports: [
    CommonModule,
    SchedulerComponent,
    BeautySchedulerResourceTemplateDirective,
    BeautySchedulerEventTemplateDirective,
    BeautySchedulerEventPopoverTemplateDirective,
    NzButtonComponent,
    NzIconDirective,
    NzSpaceComponent,
    NzTooltipDirective,
    NzSpaceModule,
    NzSpaceCompactComponent,
    RouterLink,
    NzDrawerModule,
    NzModalModule,
    InputModule,
    NzButtonModule,
    NzLayoutModule,
    TranslatePipe,
    FormsModule,
    NzGridModule,
    NgxPermissionsModule,
    DatepickerComponent,
    VisitStatusPipe,
    NzDropDownModule,
    NzMenuModule,
  ],
  templateUrl: './scheduler-calendar.component.html',
  styleUrls: ['./scheduler-calendar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SchedulerCalendarComponent {
  private readonly employeeService = inject(EmployeeService);
  private readonly bookingService = inject(BookingService);
  private readonly visitService = inject(VisitService);
  private readonly translateService = inject(TranslateService);
  private store = inject(Store);
  private readonly msg = inject(NzMessageService);
  private readonly drawerService = inject(NzDrawerService);
  private readonly modalService = inject(NzModalService);
  private readonly contextMenuService = inject(NzContextMenuService);
  private readonly router = inject(Router);

  // Right-click quick-actions menu state
  readonly slotMenu = viewChild.required<NzDropdownMenuComponent>('slotMenu');
  readonly contextSlot = signal<{
    resourceId: string;
    start: Date;
    end: Date;
  } | null>(null);

  branchSettings = this.store.selectSignal(AuthState.branchSettings);
  activeBranch = this.store.selectSignal(AuthState.getActiveBranch);
  visitBookingEnabled = signal<boolean>(false);
  viewMode = signal<SchedulerViewMode>('horizontal');
  timeInterval = toSignal(
    this.store.select(AppointmentState.getCalendarInterval).pipe(
      map((i) => {
        if (i) {
          const [hour, minute] = i.split(':').map(Number);
          if (hour < 0 || hour > 23 || minute < 0 || minute > 59) {
            return 15;
          } else if (hour >= 1) {
            return +hour * 60;
          } else {
            return +minute;
          }
        } else {
          const timeStr = `00:15:00`;
          this.store.dispatch(new CalendarTimeInterval(timeStr));
          return 15;
        }
      })
    ),
    {
      initialValue: 15,
    }
  );

  slotMinutes = linkedSignal(() => this.timeInterval());

  currentDate = signal<Date>(new Date());
  start = signal<Date>(new Date(new Date().setHours(6, 0, 0, 0)));
  end = signal<Date>(new Date(new Date().setHours(22, 0, 0, 0)));

  calendarEmployeeResource = rxResource({
    params: () => ({
      data: format(this.currentDate(), 'yyyy-MM-dd'),
    }),
    stream: ({ params }) =>
      this.employeeService.getEmployeeToCalendar(params.data).pipe(
        map((res) => {
          return res.map((emp) => {
            let workStart = new Date(new Date().setHours(9, 0, 0, 0));
            let workEnd = new Date(new Date().setHours(18, 0, 0, 0));
            // If schedule date is today, adjust workStart to be max(current time, 9
            if (emp.schedule?.shiftStart && emp.schedule?.shiftEnd) {
              const shiftDate = new Date(emp.schedule.date);
              const [shiftStartHour, shiftStartMinute] = emp.schedule.shiftStart
                .split(':')
                .map(Number);
              const [shiftEndHour, shiftEndMinute] = emp.schedule.shiftEnd
                .split(':')
                .map(Number);
              const scheduledWorkStart = new Date(
                shiftDate.setHours(shiftStartHour, shiftStartMinute, 0, 0)
              );
              const scheduledWorkEnd = new Date(
                shiftDate.setHours(shiftEndHour, shiftEndMinute, 0, 0)
              );

              workStart = new Date(scheduledWorkStart);
              workEnd = new Date(scheduledWorkEnd);
            }
            // Map breaks to Date objects on the correct date
            const breaks =
              emp.schedule?.breaks?.map((b) => ({
                start: new Date(`${emp.schedule?.date}T${b.start}`),
                end: new Date(`${emp.schedule?.date}T${b.end}`),
                title: b.type,
                color: 'rgba(255, 193, 7, 0.25)',
              })) || [];
            return {
              id: emp.id,
              userId: emp.userId,
              title: emp.fullName,
              workStart: workStart,
              workEnd: workEnd,
              breaks: breaks,
            } as SchedulerResource;
          });
        })
      ),
  });

  resources = computed<SchedulerResource[]>(
    () => this.calendarEmployeeResource.value() || []
  );

  eventResource = rxResource({
    params: () => ({
      date: format(this.currentDate(), 'yyyy-MM-dd'),
    }),
    stream: ({ params }) =>
      this.bookingService
        .getCalendarBookingNewV2({
          date: params.date,
        })
        .pipe(
          map((res) => {
            const anonymousLabel = (name?: string) => {
              const unknown = this.translateService.instant(
                'calendar.unknownClient'
              );
              return name ? `${name} (${unknown})` : `(${unknown})`;
            };
            return res.flatMap((m: CalendarBookingItem) => {
              const backgroundColor = m.visit?.id
                ? m.bookingServices && m.bookingServices.length
                  ? m.bookingServices[m.bookingServices.length - 1]?.service
                      ?.serviceCategory?.color
                  : null
                : m.service?.serviceCategory?.color || null;

              const services = m.visit?.id
                ? m.bookingServices?.length
                  ? m.bookingServices.map((s) => s.service?.name).join(', ')
                  : ''
                : m.services?.map((s) => s.service?.name).join(', ');

              // Create event for primary employee
              const primaryEvent = {
                id: m.id,
                resourceId: m.employee?.id ?? '',
                start: transformDateTime(m.bookingDate, m.startTime),
                end: transformDateTime(m.bookingDate, m.endTime),
                title: m.visit?.isAnonymous
                  ? anonymousLabel(m.visit?.anonymousName)
                  : m.user?.fullName,
                backgroundColor: backgroundColor,
                groupId: m.visit?.id != null ? String(m.visit.id) : '',
                oldStyle: !!m.visit?.id,
                extendedProps: {
                  bookingDate: m.bookingDate,
                  roomId: m.roomId,
                  roomName: m.room?.name,
                  employeeId: m.employee?.id,
                  employeeName: m.employee?.user?.fullName,
                  fullName: m.user?.fullName,
                  balance: m.user?.balance,
                  userId: m.user?.id,
                  mobilePhone: m.user?.mobilePhone,
                  status: m.status,
                  visitStatus: m.visit?.status || null,
                  // Which online product the booking came from: 'bookinghub',
                  // 'bookinghub_offer', later 'swoop'. Not to be confused with
                  // `source`, which is the salon's own marketing source and is
                  // never set on online bookings.
                  onlineSource: m.onlineSource ?? null,
                  onUserRequest: m.onUserRequest,
                  // beaut-621: a booking holding package units shows what was
                  // paid for them (their share of the package payment), not
                  // the catalogue price; every other booking is unchanged.
                  price: m.bookingServices?.some((bs: any) => bs?.packagePurchaseId)
                    ? +Number(computeCalendarBookingAmounts(m).price || 0).toFixed(2)
                    : m.price
                      ? +Number(m.price).toFixed(2)
                      : 0,
                  salePrice: m.salePrice ? +Number(m.salePrice).toFixed(2) : 0,
                  description: m.description,
                  createdAt: m.createdAt,
                  createdBy: m.createdBy?.fullName,
                  completedBy: m.completedBy?.fullName,
                  isAnonymous: m.visit?.isAnonymous,
                  anonymousName: anonymousLabel(m.visit?.anonymousName),
                  services: services,
                  additionalEmployees: null as string[] | null,
                },
              };

              // Collect additional employee info from bookingServices
              const additionalEmployeeMap = new Map<string, string>();
              m.bookingServices?.forEach((bs: CalendarBookingService) => {
                bs.additionalEmployees?.forEach((ae) => {
                  if (ae.employeeId && ae.employeeId !== m.employee?.id) {
                    additionalEmployeeMap.set(
                      ae.employeeId,
                      ae.employee?.user?.fullName || ''
                    );
                  }
                });
              });

              // Add additional employee names to primary event
              const additionalEmployeeNames = Array.from(
                additionalEmployeeMap.values()
              ).filter(Boolean);
              if (additionalEmployeeNames.length) {
                primaryEvent.extendedProps.additionalEmployees =
                  additionalEmployeeNames;
              }

              // Create events for additional employees
              const additionalEvents = Array.from(
                additionalEmployeeMap.keys()
              ).map((employeeId) => ({
                ...primaryEvent,
                id: `${m.id}-${employeeId}`,
                resourceId: employeeId,
                extendedProps: {
                  ...primaryEvent.extendedProps,
                  isAdditionalEmployee: true,
                },
              }));

              return [primaryEvent, ...additionalEvents];
            });
          })
        ),
  });

  // Params intentionally key ONLY on the visible date, not on a poll tick: a
  // tick in params would give rxResource a new params identity every 60s,
  // which discards the previous value and makes it undefined mid-refetch —
  // holds would blink out and back every minute. Polling instead calls
  // holdsResource.reload() (see constructor), which keeps the last-known
  // value visible while the refetch is in flight.
  holdsResource = rxResource({
    params: () => ({
      date: format(this.currentDate(), 'yyyy-MM-dd'),
    }),
    stream: ({ params }) =>
      this.bookingService.getDayHolds({ date: params.date }).pipe(
        map((res) =>
          (res.holds || []).map((hold): SchedulerEvent => {
            const start = transformDateTime(hold.date, hold.start);
            const end = transformDateTime(hold.date, hold.end);
            const sourceLabel = this.holdSourceLabel(hold.source);
            return {
              id: `hold-${hold.id}`,
              resourceId: hold.employeeId,
              groupId: '',
              start,
              end,
              title: sourceLabel,
              oldStyle: false,
              extendedProps: {
                isHold: true,
                source: hold.source,
                expiresAt: hold.expiresAt,
                // Pre-translated here (this component already owns the
                // ngx-translate dependency and translates everything else
                // in this template) so the generic SchedulerComponent never
                // has to know about i18n — it just renders the string.
                ariaLabel: this.translateService.instant('calendar.hold.ariaLabel', {
                  source: sourceLabel,
                  start: format(new Date(start), 'HH:mm'),
                  end: format(new Date(end), 'HH:mm'),
                }),
              },
            };
          })
        ),
        // A hold-fetch failure must never take down the booking calendar.
        // Angular resources THROW from value() while in the error state
        // (RESOURCE_VALUE_THROWS_ERRORS_DEFAULT), so an uncaught error here
        // would blow up the `events` computed below and blank the whole
        // Day Form — bookings included. Degrade to "no holds shown" instead.
        catchError(() => of([] as SchedulerEvent[]))
      ),
  });

  // 'bookinghub'/'swoop' are brand names and stay untranslated, matching how
  // they're already shown on the event card; only the generic fallback for
  // an unrecognised source needs a translation.
  private holdSourceLabel(source: DayHold['source']): string {
    switch (source) {
      case 'bookinghub':
        return 'BookingHub';
      case 'swoop':
        return 'Swoop';
      default:
        return this.translateService.instant('calendar.hold.genericSource');
    }
  }

  readonly events = computed<SchedulerEvent[]>(() => [
    ...(this.eventResource.value() || []),
    ...(this.holdsResource.value() || []),
  ]);

  resourcePrices = computed<{ resourceId: string; totalPrice: number }[]>(
    () => {
      const sums = new Map<string, number>();
      for (const ev of this.events()) {
        const price = Number(ev.extendedProps?.['price']) || 0;
        sums.set(ev.resourceId, (sums.get(ev.resourceId) || 0) + price);
      }
      return Array.from(sums, ([resourceId, total]) => ({
        resourceId,
        totalPrice: +total.toFixed(2),
      }));
    }
  );
  resourceTotalPrices = computed(() => {
    return this.resourcePrices().reduce((acc, item) => {
      acc[item.resourceId] = item.totalPrice;
      return acc;
    }, {} as Record<string, number>);
  });

  constructor() {
    // Poll day-holds every 60s so a hold that expired server-side (default
    // 900s TTL) doesn't keep showing as blocked indefinitely. reload() (not a
    // params change) keeps the previous holds visible while the refetch is
    // in flight, instead of clearing them for the duration of the request.
    // takeUntilDestroyed stops the interval — and therefore the polling —
    // when this component is destroyed, so no leaked timer keeps firing
    // after navigating away.
    interval(60000)
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.holdsResource.reload());

    // Keep the visible time range in sync with the active branch working hours.
    effect(() => {
      if (this.activeBranch()) {
        const startHour = +`0${this.activeBranch()?.whStart}`.slice(-2);
        const endHour = +`0${this.activeBranch()?.whEnd}`.slice(-2);
        this.start.set(this.dateAt(this.currentDate(), startHour, 0));
        this.end.set(this.dateAt(this.currentDate(), endHour, 0));
        this.visitBookingEnabled.set(
          this.activeBranch()?.visitBookingEnabled || false
        );
      }
    });
  }

  /** Returns a new Date on the same day as `base` at the given time (never mutates `base`). */
  private dateAt(base: Date, hours: number, minutes: number): Date {
    const d = new Date(base);
    d.setHours(hours, minutes, 0, 0);
    return d;
  }

  toggleView(): void {
    this.viewMode.update((v) =>
      v === 'horizontal' ? 'vertical' : 'horizontal'
    );
  }

  changeView(mode: SchedulerViewMode) {
    this.viewMode.set(mode);
  }

  today() {
    const now = new Date();
    this.currentDate.set(now);
    this.start.set(
      this.dateAt(now, this.start().getHours(), this.start().getMinutes())
    );
    this.end.set(
      this.dateAt(now, this.end().getHours(), this.end().getMinutes())
    );
  }

  changeDate($event: any) {
    this.currentDate.set($event);
    this.start.set(
      this.dateAt($event, this.start().getHours(), this.start().getMinutes())
    );
    this.end.set(
      this.dateAt($event, this.end().getHours(), this.end().getMinutes())
    );
  }

  onEventClick(event: SchedulerEvent) {
    const oldStyle = event.oldStyle;
    const groupId = event.groupId;
    if (!oldStyle) {
      const modal = this.modalService.create({
        nzClosable: false,
        nzMaskClosable: false,
        nzTitle: this.translateService.instant('calendar.planVisit'),
        nzContent: AddAppointmentComponent,
        nzWidth: '1100px',
        nzStyle: { top: '0px' },
        nzBodyStyle: { padding: '0' },
        nzFooter: null,
        nzData: {
          bookingId: event.id,
        },
      });
      modal.afterClose.pipe().subscribe((res) => {
        // this.visitAddFacade.reset();
        this.store.dispatch(new ClearAddAppointmentSpecService());
        // this.getCalendarEmployees(this.currentDate);
        this.eventResource.reload();
      });
    } else {
      const modal = this.drawerService.create({
        nzClosable: true,
        nzMaskClosable: false,
        nzTitle: this.translateService.instant('calendar.planVisit'),
        nzContent: VisitManageComponent,
        nzWidth: window.innerWidth <= 1024 ? '100%' : '70%',
        nzBodyStyle: { padding: '0' },
        nzData: {
          visitId: groupId,
        },
      });

      modal.afterClose.pipe().subscribe((res) => {
        // this.visitAddFacade.reset();
        this.store.dispatch(new ClearAddAppointmentSpecService());
        // this.getCalendarEmployees(this.currentDate);
        this.eventResource.reload();
      });
    }
  }

  onSlotClick(payload: { resourceId: string; start: Date; end: Date }) {
    const resourceId = payload.resourceId;
    const startStr = payload.start;
    if (this.visitBookingEnabled()) {
      this.openVisitDrawer(resourceId, startStr);
    } else {
      this.oldBookingModal(resourceId, startStr);
    }
  }

  private openVisitDrawer(resourceId: string, date: Date) {
    const modal = this.drawerService.create({
      nzClosable: true,
      nzMaskClosable: false,
      nzTitle: this.translateService.instant('calendar.planVisit'),
      nzContent: VisitManageComponent,
      nzWidth: window.innerWidth <= 1024 ? '100%' : '70%',
      nzBodyStyle: { padding: '0' },
      nzData: {
        employeeId: resourceId,
        date,
      },
    });

    modal.afterClose.pipe().subscribe(() => {
      this.store.dispatch(new ClearAddAppointmentSpecService());
      this.eventResource.reload();
    });
  }

  oldBookingModal(resourceId: string, startStr: Date) {
    const modal = this.modalService.create({
      nzClosable: true,
      nzMaskClosable: false,
      nzTitle: this.translateService.instant('calendar.planVisit'),
      nzContent: AddAppointmentComponent,
      nzWidth: '1100px',
      nzStyle: { top: '0px' },
      nzBodyStyle: { padding: '0' },
      nzFooter: null,
      nzData: {
        employeeId: resourceId,
        date: startStr,
      },
    });

    modal.afterClose.pipe().subscribe((res) => {
      this.store.dispatch(new ClearAddAppointmentSpecService());
      this.eventResource.reload();
      // this.getCalendarBooking({ date: this.currentDate });
    });
  }

  // ---- Right-click quick-actions menu ----
  onSlotContextMenu(payload: {
    resourceId: string;
    start: Date;
    end: Date;
    available: boolean;
    originalEvent: MouseEvent;
  }) {
    this.contextSlot.set({
      resourceId: payload.resourceId,
      start: payload.start,
      end: payload.end,
    });
    this.contextMenuService.create(payload.originalEvent, this.slotMenu());
  }

  private closeContextMenu() {
    this.contextMenuService.close();
  }

  ctxNewVisit() {
    const slot = this.contextSlot();
    this.closeContextMenu();
    if (!slot) return;
    this.openVisitDrawer(slot.resourceId, slot.start);
  }

  ctxGoToEmployeeDay() {
    const slot = this.contextSlot();
    this.closeContextMenu();
    if (!slot) return;
    const resource = this.resources().find((r) => r.id === slot.resourceId);
    if (resource?.userId) {
      this.router.navigate(['/calendars/day/forEmployee', resource.userId]);
    }
  }

  ctxBlockTime() {
    const slot = this.contextSlot();
    this.closeContextMenu();
    if (!slot) return;
    const resource = this.resources().find((r) => r.id === slot.resourceId);
    const date = format(slot.start, 'yyyy-MM-dd');
    this.modalService.confirm({
      nzTitle: this.translateService.instant('calendar.menu.block'),
      nzContent: this.translateService.instant('calendar.menu.blockConfirm', {
        name: resource?.title ?? '',
        date,
      }),
      nzOkDanger: true,
      nzOnOk: () =>
        new Promise<void>((resolve) => {
          this.employeeService
            .dayOff({ employeeId: slot.resourceId, date })
            .subscribe({
              next: () => {
                this.msg.success(
                  this.translateService.instant('calendar.menu.blockSuccess')
                );
                this.calendarEmployeeResource.reload();
                this.eventResource.reload();
                resolve();
              },
              error: (err) => {
                this.msg.error(
                  err?.error?.message ||
                    this.translateService.instant('calendar.saveError')
                );
                resolve();
              },
            });
        }),
    });
  }

  onEventDrop(payload: {
    event: SchedulerEvent;
    resourceId: string;
    start: Date;
    end: Date;
  }) {
    // Drag can change the employee (resource), so use the drop target resource.
    this.persistEventChange(
      payload.event,
      payload.resourceId,
      payload.start,
      payload.end
    );
  }

  onEventResize(payload: {
    event: SchedulerEvent;
    resourceId: string;
    start: Date;
    end: Date;
  }) {
    // Resize only changes the time; the employee (resource) stays the same.
    this.persistEventChange(
      payload.event,
      payload.event.resourceId,
      payload.start,
      payload.end
    );
  }

  /**
   * Persist a moved/resized booking. Visit-backed bookings go through the visit
   * reschedule endpoint; standalone bookings use the booking update endpoint.
   * Reloads events on success and snaps back (reload) on error.
   */
  private persistEventChange(
    event: SchedulerEvent,
    resourceId: string,
    start: Date,
    end: Date
  ) {
    // Additional-employee events are read-only mirrors of the primary booking.
    if (event.extendedProps?.['isAdditionalEmployee']) {
      this.eventResource.reload();
      return;
    }

    const request$ = event.groupId
      ? this.visitService.rescheduleVisit({
          visitId: +event.groupId,
          events: [
            {
              id: event.id!,
              start: format(start, 'yyyy-MM-dd HH:mm'),
              end: format(end, 'yyyy-MM-dd HH:mm'),
              employeeId: resourceId,
            },
          ],
        })
      : this.bookingService.updateNew(event.id, {
          bookingDate: format(start, 'yyyy-MM-dd'),
          startTime: format(start, 'HH:mm'),
          endTime: format(end, 'HH:mm'),
          employeeId: resourceId,
        });

    request$.subscribe({
      next: () => this.eventResource.reload(),
      error: (err) => {
        this.msg.error(
          err?.error?.message ||
            this.translateService.instant('calendar.saveError')
        );
        this.eventResource.reload();
      },
    });
  }

  updateSlotMinutes(val: number) {
    const minutes = Math.max(5, Math.min(120, Math.round(val)));
    this.slotMinutes.set(minutes);
    let timeStr = `00:${minutes < 10 ? '0' : ''}${minutes}:00`;
    if (val >= 60) {
      const hour = Math.floor(val / 60);
      const minute = val % 60;
      timeStr = `${hour}:${minute < 10 ? '0' : ''}${minute}:00`;
    }
    this.store.dispatch(new CalendarTimeInterval(timeStr));
  }

  shiftDay(days: number) {
    this.start.update((d) => new Date(d.getTime() + days * 86400000));
    this.end.update((d) => new Date(d.getTime() + days * 86400000));
    this.currentDate.set(this.start());
  }

  employeeCard(resource: SchedulerResource) {
    const drawerRef = this.drawerService.create<
      EmployeeViewComponent,
      { employeeId: string },
      string
    >({
      nzTitle: resource.title,
      nzContent: EmployeeViewComponent,
      nzWrapClassName: 'user-drawer',
      nzBodyStyle: {
        padding: '0 15px 15px 15px',
      },
      nzData: {
        employeeId: resource.id,
      },
    });

    // The employee view lets the user change the schedule / day-off. Refresh the
    // calendar on close so those changes show without a manual page reload — the
    // drawer previously closed without reloading anything (beaut-368).
    drawerRef.afterClose.subscribe(() => {
      this.calendarEmployeeResource.reload();
      this.eventResource.reload();
    });
  }

  orderResources() {
    const drawerRef =
      this.drawerService.create<AppointmentResourceOrderComponent>({
        nzTitle: this.translateService.instant('calendar.sortEmployee'),
        nzContent: AppointmentResourceOrderComponent,
        nzWidth: '500px',
      });

    drawerRef.afterClose.pipe().subscribe((data) => {
      this.calendarEmployeeResource.reload();
    });
  }
}
